import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  Save,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

function safeErrorMessage(error) {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Unable to update QA Proxy policy.';
  const lowered = message.toLowerCase();
  if (lowered.includes('traceback') || lowered.includes('password') || lowered.includes('secret')) {
    return 'Unable to update QA Proxy policy.';
  }
  return message;
}

const DEFAULTS = {
  webAccessMode: 'full_web',
  allowClientOverride: false,
  allowedProtocols: ['http', 'https'],
  tokenTtlSeconds: '120',
  maxSessionDurationSeconds: '3600',
  defaultClientQuotaBytes: '',
  defaultUserQuotaBytes: '',
  killSwitchEnabled: false,
  dataimpulseRoutingDisabled: false,
};

function formFromPolicy(policy) {
  const protocols = policy?.allowedProtocols || policy?.allowed_protocols || ['http', 'https'];
  return {
    webAccessMode: policy?.webAccessMode || policy?.web_access_mode || 'full_web',
    allowClientOverride: Boolean(
      policy?.allowClientOverride ?? policy?.allow_client_override ?? false,
    ),
    allowedProtocols: Array.isArray(protocols) ? [...protocols] : ['http', 'https'],
    tokenTtlSeconds: String(
      policy?.tokenTtlSeconds ?? policy?.token_ttl_seconds ?? 120,
    ),
    maxSessionDurationSeconds: String(
      policy?.maxSessionDurationSeconds ?? policy?.max_session_duration_seconds ?? 3600,
    ),
    defaultClientQuotaBytes: (policy?.defaultClientQuotaBytes != null
      || policy?.default_client_quota_bytes != null)
      ? String(policy.defaultClientQuotaBytes ?? policy.default_client_quota_bytes)
      : '',
    defaultUserQuotaBytes: (policy?.defaultUserQuotaBytes != null
      || policy?.default_user_quota_bytes != null)
      ? String(policy.defaultUserQuotaBytes ?? policy.default_user_quota_bytes)
      : '',
    killSwitchEnabled: Boolean(
      policy?.killSwitchEnabled ?? policy?.kill_switch_enabled ?? false,
    ),
    dataimpulseRoutingDisabled: Boolean(
      policy?.dataimpulseRoutingDisabled ?? policy?.dataimpulse_routing_disabled ?? false,
    ),
  };
}

const QaProxyPolicyPage = () => {
  const { getAuthHeaders } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadPolicy = async () => {
    setLoading(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.getQaProxyPolicy();
      setPolicy(payload);
      setForm(formFromPolicy(payload));
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicy();
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleProtocol = (protocol) => {
    setForm((current) => {
      const has = current.allowedProtocols.includes(protocol);
      const next = has
        ? current.allowedProtocols.filter((item) => item !== protocol)
        : [...current.allowedProtocols, protocol];
      return { ...current, allowedProtocols: next.length ? next : current.allowedProtocols };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const body = {
        web_access_mode: form.webAccessMode,
        allow_client_override: Boolean(form.allowClientOverride),
        allowed_protocols: form.allowedProtocols,
        token_ttl_seconds: Number(form.tokenTtlSeconds) || 120,
        max_session_duration_seconds: Number(form.maxSessionDurationSeconds) || 3600,
        kill_switch_enabled: Boolean(form.killSwitchEnabled),
        dataimpulse_routing_disabled: Boolean(form.dataimpulseRoutingDisabled),
      };
      if (form.defaultClientQuotaBytes !== '') {
        body.default_client_quota_bytes = Number(form.defaultClientQuotaBytes) || 0;
      }
      if (form.defaultUserQuotaBytes !== '') {
        body.default_user_quota_bytes = Number(form.defaultUserQuotaBytes) || 0;
      }
      const payload = await adminOpsService.putQaProxyPolicy(body);
      setPolicy(payload);
      setForm(formFromPolicy(payload));
      toast.success('QA Proxy policy saved');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const isRestricted = form.webAccessMode === 'restricted_allowlist';
  const modeLabel = isRestricted ? 'Restricted allowlist' : 'Full web access';

  return (
    <div className="space-y-6" data-testid="qa-proxy-policy-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="text-amber-400" size={28} />
            QA Proxy Policy
          </h1>
          <p className="text-slate-500 mt-1">
            Global web access mode, quotas, and kill switches for Authorized QA Proxy.
          </p>
        </div>
        <Button variant="outline" onClick={loadPolicy} disabled={loading || busy}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error ? (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4"
          data-testid="qa-proxy-policy-error"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, index) => <Skeleton key={index} className="h-28 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Web access mode</p>
            <p className="text-lg font-semibold text-white" data-testid="qa-proxy-policy-mode-summary">
              {modeLabel}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Kill switch</p>
            <p
              className={`text-lg font-semibold ${form.killSwitchEnabled ? 'text-amber-400' : 'text-emerald-400'}`}
              data-testid="qa-proxy-policy-kill-summary"
            >
              {form.killSwitchEnabled ? 'Sessions suspended' : 'Sessions allowed'}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">DataImpulse routing</p>
            <p
              className={`text-lg font-semibold ${form.dataimpulseRoutingDisabled ? 'text-amber-400' : 'text-emerald-400'}`}
              data-testid="qa-proxy-policy-routing-summary"
            >
              {form.dataimpulseRoutingDisabled ? 'Disabled' : 'Enabled'}
            </p>
          </div>
        </div>
      )}

      <form
        className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-5"
        onSubmit={handleSave}
        data-testid="qa-proxy-policy-form"
      >
        <label className="block space-y-1">
          <span className="text-sm text-slate-400">QA Web Access Mode</span>
          <select
            className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
            value={form.webAccessMode}
            onChange={handleChange('webAccessMode')}
            data-testid="qa-proxy-policy-web-access-mode"
          >
            <option value="full_web">Full web access through configured proxy</option>
            <option value="restricted_allowlist">Restricted allowlist</option>
          </select>
        </label>

        {isRestricted ? (
          <div
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3"
            data-testid="qa-proxy-policy-restricted-note"
          >
            <p className="text-sm text-amber-100">
              Restricted mode limits QA sessions to the administrator-approved domain allowlist.
              Manage that list on the QA Website Access page.
            </p>
            <a
              href="/admin/qa-website-access"
              className="inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300"
              data-testid="qa-proxy-policy-allowlist-link"
            >
              Open QA Website Access
              <ExternalLink size={14} />
            </a>
          </div>
        ) : null}

        <label className="flex items-center gap-3 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={form.allowClientOverride}
            onChange={handleChange('allowClientOverride')}
            data-testid="qa-proxy-policy-client-override"
          />
          Allow client/user override (default Off)
        </label>

        <fieldset className="space-y-2" data-testid="qa-proxy-policy-protocols">
          <legend className="text-sm text-slate-400">Allowed protocols</legend>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.allowedProtocols.includes('http')}
              onChange={() => toggleProtocol('http')}
              data-testid="qa-proxy-policy-protocol-http"
            />
            HTTP
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.allowedProtocols.includes('https')}
              onChange={() => toggleProtocol('https')}
              data-testid="qa-proxy-policy-protocol-https"
            />
            HTTPS
          </label>
        </fieldset>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="space-y-1">
            <span className="text-sm text-slate-400">Token TTL (seconds)</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              type="number"
              min="30"
              max="600"
              value={form.tokenTtlSeconds}
              onChange={handleChange('tokenTtlSeconds')}
              data-testid="qa-proxy-policy-token-ttl"
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm text-slate-400">Max QA session duration (seconds)</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              type="number"
              min="60"
              max="86400"
              value={form.maxSessionDurationSeconds}
              onChange={handleChange('maxSessionDurationSeconds')}
              data-testid="qa-proxy-policy-max-session"
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm text-slate-400">Default client traffic quota (bytes)</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              type="number"
              min="0"
              value={form.defaultClientQuotaBytes}
              onChange={handleChange('defaultClientQuotaBytes')}
              placeholder="Optional"
              data-testid="qa-proxy-policy-client-quota"
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm text-slate-400">Default per-user traffic quota (bytes)</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              type="number"
              min="0"
              value={form.defaultUserQuotaBytes}
              onChange={handleChange('defaultUserQuotaBytes')}
              placeholder="Optional"
              data-testid="qa-proxy-policy-user-quota"
            />
          </label>
        </div>

        <div className="space-y-3 border-t border-stone-800 pt-4">
          <p className="text-sm font-medium text-slate-300">Kill switches</p>
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.killSwitchEnabled}
              onChange={handleChange('killSwitchEnabled')}
              data-testid="qa-proxy-policy-kill-switch"
            />
            Suspend all QA proxy sessions
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.dataimpulseRoutingDisabled}
              onChange={handleChange('dataimpulseRoutingDisabled')}
              data-testid="qa-proxy-policy-routing-disabled"
            />
            Disable DataImpulse routing
          </label>
        </div>

        <Button type="submit" disabled={busy || loading} data-testid="qa-proxy-policy-save">
          <Save size={16} className="mr-2" />
          {busy ? 'Saving…' : 'Save policy'}
        </Button>

        {policy ? (
          <p className="text-xs text-slate-500" data-testid="qa-proxy-policy-source-hint">
            Policy source: Admin Control Center
          </p>
        ) : null}
      </form>
    </div>
  );
};

export default QaProxyPolicyPage;
